'use client';

import React, { useEffect, useRef, useState } from 'react';

function useInView(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          observer.disconnect();
        }
      },
      { threshold }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, inView };
}

interface Testimonial {
  quote: string;
  name: string;
  title: string;
  company: string;
  initials: string;
  color: string;
}

const testimonials: Testimonial[] = [
  {
    quote: 'Pluto7 Consulting helped us completely rethink our operational strategy. Their team brought a level of rigor and insight that we simply could not find elsewhere. The results exceeded our expectations.',
    name: 'Michael R.',
    title: 'Chief Operating Officer',
    company: 'Technology Enterprise',
    initials: 'MR',
    color: '#4284F5',
  },
  {
    quote: 'The strategic roadmap Pluto7 developed for our digital transformation was comprehensive and actionable. Their consultants understood our industry deeply and delivered practical recommendations we could implement immediately.',
    name: 'Sarah L.',
    title: 'VP of Strategy',
    company: 'Manufacturing Corp',
    initials: 'SL',
    color: '#00BDA5',
  },
  {
    quote: 'Working with Pluto7 Consulting was a game-changer for our organization. Their research and analysis capabilities are exceptional, and they helped us identify market opportunities we had completely overlooked.',
    name: 'David K.',
    title: 'CEO',
    company: 'Growth-Stage Company',
    initials: 'DK',
    color: '#8B5CF6',
  },
  {
    quote: 'The management consulting engagement with Pluto7 transformed how our teams operate. They brought structure, clarity, and accountability to our processes. Our efficiency improved dramatically within months.',
    name: 'Jennifer T.',
    title: 'Director of Operations',
    company: 'Healthcare Organization',
    initials: 'JT',
    color: '#F59E0B',
  },
];

const industryCards = [
  { name: 'Technology', icon: '💻', description: 'Digital transformation and IT strategy' },
  { name: 'Manufacturing', icon: '🏭', description: 'Operational efficiency and process optimization' },
  { name: 'Healthcare', icon: '🏥', description: 'Compliance, operations, and growth strategy' },
  { name: 'Financial Services', icon: '🏦', description: 'Risk management and regulatory advisory' },
  { name: 'Retail & CPG', icon: '🛍️', description: 'Market strategy and supply chain consulting' },
  { name: 'Energy & Utilities', icon: '⚡', description: 'Infrastructure and sustainability consulting' },
];

export default function SocialProofSection() {
  const { ref, inView } = useInView();
  const { ref: industryRef, inView: industryInView } = useInView();
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial(prev => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      {/* Testimonials Section */}
      <section
        className="py-24"
        style={{ background: '#FFFFFF' }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div
            ref={ref}
            className={`text-center mb-16 transition-all duration-700 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold mb-4"
              style={{
                background: 'rgba(66, 132, 245, 0.1)',
                border: '1px solid rgba(66, 132, 245, 0.2)',
                color: '#4284F5',
              }}
            >
              Client Testimonials
            </div>
            <h2
              className="font-bold text-navy-dark mb-4"
              style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}
            >
              Trusted by Business Leaders
              <span
                className="block"
                style={{
                  background: 'linear-gradient(135deg, #4284F5, #21427B)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Across Industries
              </span>
            </h2>
            <p className="text-gray-500 text-lg max-w-2xl mx-auto">
              Our clients trust us to deliver strategic insights and practical solutions that
              drive real business outcomes.
            </p>
          </div>

          {/* Testimonials grid */}
          <div
            className={`grid md:grid-cols-2 gap-6 mb-12 transition-all duration-700 delay-100 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {testimonials.map((testimonial, i) => (
              <div
                key={i}
                className="p-7 rounded-card transition-all duration-300 hover:-translate-y-1"
                style={{
                  background: activeTestimonial === i
                    ? `linear-gradient(135deg, ${testimonial.color}08, ${testimonial.color}15)`
                    : 'white',
                  border: `1px solid ${activeTestimonial === i ? testimonial.color + '30' : 'rgba(66, 132, 245, 0.08)'}`,
                  boxShadow: activeTestimonial === i
                    ? `0 8px 40px ${testimonial.color}15`
                    : '0 2px 16px rgba(66, 132, 245, 0.06)',
                  cursor: 'pointer',
                }}
                onClick={() => setActiveTestimonial(i)}
              >
                {/* Quote mark */}
                <div
                  className="text-5xl font-black leading-none mb-4"
                  style={{ color: testimonial.color, opacity: 0.3 }}
                >
                  &ldquo;
                </div>

                {/* Stars */}
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, j) => (
                    <svg key={j} className="w-4 h-4" style={{ color: '#F59E0B' }} fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>

                <p className="text-gray-600 text-sm leading-relaxed mb-6 italic">
                  &ldquo;{testimonial.quote}&rdquo;
                </p>

                <div className="flex items-center gap-3">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0"
                    style={{ background: `linear-gradient(135deg, ${testimonial.color}, ${testimonial.color}99)` }}
                  >
                    {testimonial.initials}
                  </div>
                  <div>
                    <div className="font-semibold text-navy-dark text-sm">{testimonial.name}</div>
                    <div className="text-gray-400 text-xs">{testimonial.title}, {testimonial.company}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2">
            {testimonials.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveTestimonial(i)}
                className="rounded-full transition-all duration-300"
                style={{
                  width: activeTestimonial === i ? '24px' : '8px',
                  height: '8px',
                  background: activeTestimonial === i ? '#4284F5' : '#D1D5DB',
                }}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Industries Section */}
      <section
        className="py-20"
        style={{
          background: 'linear-gradient(180deg, #F7FAFF 0%, #EEF4FF 100%)',
        }}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div
            ref={industryRef}
            className={`text-center mb-12 transition-all duration-700 ${
              industryInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <h2
              className="font-bold text-navy-dark mb-3"
              style={{ fontSize: 'clamp(24px, 3vw, 36px)' }}
            >
              Industries We Serve
            </h2>
            <p className="text-gray-500">Cross-sector expertise delivering value across diverse industries</p>
          </div>

          <div
            className={`grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 transition-all duration-700 delay-100 ${
              industryInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            {industryCards.map((industry, i) => (
              <div
                key={i}
                className="p-5 rounded-card text-center transition-all duration-300 hover:-translate-y-1 cursor-pointer group"
                style={{
                  background: 'white',
                  border: '1px solid rgba(66, 132, 245, 0.1)',
                  boxShadow: '0 2px 12px rgba(66, 132, 245, 0.05)',
                  transitionDelay: `${i * 50}ms`,
                }}
                onMouseEnter={(e) => {
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 30px rgba(66, 132, 245, 0.15)';
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(66, 132, 245, 0.3)';
                }}
                onMouseLeave={(e) => {
                  (e.currentTarget as HTMLDivElement).style.boxShadow = '0 2px 12px rgba(66, 132, 245, 0.05)';
                  (e.currentTarget as HTMLDivElement).style.borderColor = 'rgba(66, 132, 245, 0.1)';
                }}
              >
                <div className="text-3xl mb-3">{industry.icon}</div>
                <div className="font-semibold text-navy-dark text-sm mb-1">{industry.name}</div>
                <div className="text-gray-400 text-xs leading-relaxed">{industry.description}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
